import { useMemo, useState } from "react"
import { useChat } from "@ai-sdk/react"
import { DefaultChatTransport } from "ai"

export default function useChatbot({ showInlineCards = false, agentId, entrypoint, agendaReady = false, programPublished = false } = {}) {
    // One id per conversation, stable across every turn and regenerated only when
    // the conversation resets. Sent on each request so the backend can group all
    // turns of this chat into a single Portkey trace.
    const [conversationId, setConversationId] = useState(() => crypto.randomUUID())

    const chatTransport = useMemo(
        () =>
            new DefaultChatTransport({
                api: "/api/ai/chat",
                body: { showInlineCards, agentId, entrypoint, conversationId },
            }),
        [showInlineCards, agentId, entrypoint, conversationId],
    )

    const { messages, setMessages, sendMessage, status: chatStatus, error, clearError } = useChat({
        transport: chatTransport,
        id: `chat-${agentId ?? "none"}`,
    })
    const [input, setInput] = useState("")
    const isLoading = chatStatus === "submitted" || chatStatus === "streaming"
    const [resetKey, setResetKey] = useState(0)

    const handleReset = () => {
        clearError()
        setMessages([])
        setInput("")
        setConversationId(crypto.randomUUID())
        setResetKey(k => k + 1)
    }

    const handleSubmit = e => {
        e?.preventDefault?.()
        const text = input.trim()
        if (!text || isLoading) return
        setInput("")
        // Pass agendaReady and programPublished per-call: useChat freezes the transport (and its static
        // body) on first render, so a static body would send the initial `false`.
        sendMessage({ text }, { body: { agendaReady, programPublished } })
    }

    const onSend = text => {
        if (isLoading) return
        sendMessage({ text }, { body: { agendaReady, programPublished } })
    }

    return { messages, input, setInput, isLoading, error, resetKey, handleReset, handleSubmit, onSend }
}
